// Traductions du mode Lite : page Rapports (bilans chiffrés par période), tiroir de génération, détail.
export const fr = {
  title: "Rapports",
  subtitle: "Un bilan chiffré de ta période : trading et prop firms.",
  generate: "Nouveau rapport",
  empty: {
    title: "Pas encore de rapport",
    text: "Génère un bilan du mois, du trimestre ou de l'année : résultat net, win rate, payouts, dépenses et ROI, sur tous tes comptes ou un seul.",
    cta: "Générer mon premier rapport",
  },
  list: {
    scopeAll: "Comptes actifs",
    generatedOn: (d: string) => `Généré le ${d}`,
    net: "Résultat net",
    trades: "Trades",
    payouts: "Payouts",
    roi: "ROI",
    delete: "Supprimer ce rapport",
    deleteConfirm: "Supprimer ce rapport ? Les chiffres ne seront pas recalculés.",
  },
  periods: { monthly: "Mois", quarterly: "Trimestre", annual: "Année" },
  drawer: {
    title: "Nouveau rapport",
    subtitle: "Choisis la période et le périmètre. Les chiffres sont figés à la génération.",
    period: "Période",
    previous: "Période précédente",
    next: "Période suivante",
    scope: "Périmètre",
    scopeActive: "Comptes actifs",
    scopeAllInactive: "Tous les comptes, inactifs compris",
    groupFirms: "Prop firms",
    groupAccounts: "Comptes",
    generating: "Calcul en cours…",
    error: "Le rapport n'a pas pu être généré. Réessaie dans un instant.",
  },
  detail: {
    back: "Tous les rapports",
    notFound: "Ce rapport n'existe pas ou n'est plus disponible.",
    legacy: "Rapport généré avec une ancienne version : la partie prop firm n'est pas disponible.",
    trading: "Trading",
    tradingSubtitle: "Trades clôturés sur la période.",
    noTrades: "Aucun trade clôturé sur cette période.",
    propFirm: "Prop firms",
    propFirmSubtitle: "Dépenses, payouts et mouvements de comptes sur la période.",
    noPropFirm: "Aucune donnée prop firm sur ce périmètre.",
    m: {
      net: "Résultat net",
      trades: "Trades",
      winRate: "Win rate",
      winRateHint: (long: number, short: number) => `${long} long · ${short} short`,
      pf: "Profit factor",
      avgTrade: "Moyenne par trade",
      expectancy: "Espérance",
      daysTraded: "Jours tradés",
      daysTradedHint: (n: number) => `sur ${n} jours ouvrés`,
      avgDay: "Moyenne par jour",
      bestTrade: "Meilleur trade",
      worstTrade: "Pire trade",
      bestDay: "Meilleur jour",
      worstDay: "Pire jour",
      drawdown: "Drawdown max",
      streaks: "Séries",
      streaksHint: "gagnante / perdante",
      grossProfit: "Gains bruts",
      grossLoss: "Pertes brutes",
    },
    symbols: {
      title: "Par instrument",
      symbol: "Instrument",
      trades: "Trades",
      winRate: "Win rate",
      net: "Net",
    },
    finance: {
      title: "Bilan financier",
      spent: "Dépensé",
      payouts: "Payouts",
      net: "Net",
      roi: "ROI",
      roiNone: "Sans dépense",
    },
    payouts: {
      title: "Payouts",
      count: (n: number) => `${n} payout${n > 1 ? "s" : ""}`,
      best: "Meilleur payout",
      none: "Aucun payout sur la période.",
      noFirm: "Sans prop firm",
    },
    lifecycle: {
      title: "Mouvements de comptes",
      added: "Ajoutés",
      blown: "Cramés",
      funded: "Passés funded",
      lowConfidence: (n: number) => `+ ${n} probablement cramé${n > 1 ? "s" : ""} (clôture sans drawdown constaté)`,
      none: "Aucun mouvement de compte sur la période.",
      unlinked: "challenge non rattaché",
      outcomesTitle: (n: number) => `Devenir des ${n} compte${n > 1 ? "s" : ""} ajouté${n > 1 ? "s" : ""}`,
      outcomes: {
        blown: "Cramés",
        likelyBlown: "Probablement cramés",
        passed: "Réussis",
        active: "En cours",
      },
    },
    advancement: {
      title: "Challenges en cours",
      days: "Jours tradés",
      complete: "Objectif atteint",
      none: "Aucun challenge en cours en fin de période.",
    },
  },
};

export const en: typeof fr = {
  title: "Reports",
  subtitle: "A numbers-only review of your period: trading and prop firms.",
  generate: "New report",
  empty: {
    title: "No report yet",
    text: "Generate a monthly, quarterly or annual review: net result, win rate, payouts, expenses and ROI, across all your accounts or just one.",
    cta: "Generate my first report",
  },
  list: {
    scopeAll: "Active accounts",
    generatedOn: (d: string) => `Generated on ${d}`,
    net: "Net result",
    trades: "Trades",
    payouts: "Payouts",
    roi: "ROI",
    delete: "Delete this report",
    deleteConfirm: "Delete this report? The figures will not be recomputed.",
  },
  periods: { monthly: "Month", quarterly: "Quarter", annual: "Year" },
  drawer: {
    title: "New report",
    subtitle: "Pick the period and the scope. Figures are frozen at generation.",
    period: "Period",
    previous: "Previous period",
    next: "Next period",
    scope: "Scope",
    scopeActive: "Active accounts",
    scopeAllInactive: "All accounts, including inactive",
    groupFirms: "Prop firms",
    groupAccounts: "Accounts",
    generating: "Computing…",
    error: "The report could not be generated. Try again in a moment.",
  },
  detail: {
    back: "All reports",
    notFound: "This report does not exist or is no longer available.",
    legacy: "Report generated with an older version: the prop firm part is not available.",
    trading: "Trading",
    tradingSubtitle: "Trades closed during the period.",
    noTrades: "No closed trades in this period.",
    propFirm: "Prop firms",
    propFirmSubtitle: "Expenses, payouts and account moves over the period.",
    noPropFirm: "No prop firm data in this scope.",
    m: {
      net: "Net result",
      trades: "Trades",
      winRate: "Win rate",
      winRateHint: (long: number, short: number) => `${long} long · ${short} short`,
      pf: "Profit factor",
      avgTrade: "Average per trade",
      expectancy: "Expectancy",
      daysTraded: "Days traded",
      daysTradedHint: (n: number) => `out of ${n} trading days`,
      avgDay: "Average per day",
      bestTrade: "Best trade",
      worstTrade: "Worst trade",
      bestDay: "Best day",
      worstDay: "Worst day",
      drawdown: "Max drawdown",
      streaks: "Streaks",
      streaksHint: "winning / losing",
      grossProfit: "Gross profit",
      grossLoss: "Gross loss",
    },
    symbols: {
      title: "By instrument",
      symbol: "Instrument",
      trades: "Trades",
      winRate: "Win rate",
      net: "Net",
    },
    finance: {
      title: "Financial summary",
      spent: "Spent",
      payouts: "Payouts",
      net: "Net",
      roi: "ROI",
      roiNone: "No expense",
    },
    payouts: {
      title: "Payouts",
      count: (n: number) => `${n} payout${n > 1 ? "s" : ""}`,
      best: "Best payout",
      none: "No payout in this period.",
      noFirm: "No prop firm",
    },
    lifecycle: {
      title: "Account moves",
      added: "Added",
      blown: "Breached",
      funded: "Went funded",
      lowConfidence: (n: number) => `+ ${n} likely breached (closed with no drawdown seen)`,
      none: "No account move in this period.",
      unlinked: "challenge not linked",
      outcomesTitle: (n: number) => `What became of the ${n} added account${n > 1 ? "s" : ""}`,
      outcomes: {
        blown: "Breached",
        likelyBlown: "Likely breached",
        passed: "Passed",
        active: "Ongoing",
      },
    },
    advancement: {
      title: "Ongoing challenges",
      days: "Days traded",
      complete: "Target reached",
      none: "No challenge in progress at the end of the period.",
    },
  },
};
